import useLocalStorage from "@rehooks/local-storage";
import { useCallback, useMemo } from "react";
import { SortDescriptor } from "react-aria-components";

import { ExternalSortManagement, SortStateDescriptor } from "components/Filters/types";
import { SearchQueryOrderDirection } from "types/generated";

type UseLocalTableSortParams = {
  localStorageKey: string;
  sortConfig: Record<string, string>;
  initialSortOption: string;
  initialSortDirection: SearchQueryOrderDirection;
};

const DIRECTION_MAP: Record<SearchQueryOrderDirection, SortDescriptor["direction"]> = {
  [SearchQueryOrderDirection.Asc]: "ascending",
  [SearchQueryOrderDirection.Desc]: "descending",
};

const useLocalTableSort = ({
  localStorageKey,
  initialSortOption,
  initialSortDirection,
  sortConfig,
}: UseLocalTableSortParams): {
  externalSortManagement: ExternalSortManagement;
  filterParams: SortDescriptor;
} => {
  const [sortState, setSortState] = useLocalStorage<SortStateDescriptor>(localStorageKey, {
    sortKey: initialSortOption,
    sortDirection: initialSortDirection,
  });

  const handleSortOptionChange = useCallback(
    (descriptor: SortStateDescriptor) => {
      if (!descriptor.sortKey) return;

      setSortState(descriptor);
    },
    [setSortState]
  );

  const filterParams = useMemo<SortDescriptor>(() => {
    const sortConfigKey = Object.entries(sortConfig).find(
      ([, value]) => value === sortState?.sortKey
    )?.[0];

    const direction = sortState?.sortDirection ?? initialSortDirection;

    return {
      column: sortConfigKey ?? initialSortOption,
      direction: DIRECTION_MAP[direction],
    };
  }, [sortState, sortConfig, initialSortDirection, initialSortOption]);

  const externalSortManagement = useMemo<ExternalSortManagement>(() => {
    return {
      sortState: sortState ?? {
        sortKey: sortConfig[initialSortOption],
        sortDirection: initialSortDirection,
      },
      applySortState: handleSortOptionChange,
    };
  }, [sortState, sortConfig, initialSortDirection, initialSortOption, handleSortOptionChange]);

  return {
    externalSortManagement,
    filterParams,
  };
};

export default useLocalTableSort;
